import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import EngineerLayout from './EngineerLayout';
import { db } from '../../firebase';
import { ref as dbRef, onValue, remove } from 'firebase/database';
import { 
    FaSearch, 
    FaEdit, 
    FaTrashAlt, 
    FaCopy,
    FaFileExcel,
    FaPrint,
    FaFilePdf,
    FaPlus,
    FaArrowLeft,
    FaCalendarAlt,
    FaBriefcase
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import * as XLSX from 'xlsx';

const EngineerWorkNames = () => {
    const navigate = useNavigate();
    const [works, setWorks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');

    useEffect(() => {
        const worksRef = dbRef(db, 'ekamwatapzp/WorkNames');
        const unsubscribe = onValue(worksRef, (snapshot) => {
            if (snapshot.exists()) {
                const data = snapshot.val();
                let flattenedWorks = [];
                
                Object.entries(data).forEach(([key, val]) => {
                    if (val && typeof val === 'object') {
                        // Check if it's a direct work object (has schemeName)
                        if (val.schemeName) {
                            flattenedWorks.push({
                                id: key,
                                category: val.category || 'General',
                                dbPath: `ekamwatapzp/WorkNames/${key}`,
                                ...val
                            });
                        } else {
                            // It's a category folder (legacy structure)
                            const category = key;
                            Object.entries(val).forEach(([id, value]) => {
                                flattenedWorks.push({
                                    id,
                                    category,
                                    dbPath: `ekamwatapzp/WorkNames/${category}/${id}`,
                                    ...value
                                });
                            });
                        }
                    }
                });
                
                setWorks(flattenedWorks.reverse()); 
            } else {
                setWorks([]);
            }
            setLoading(false);
        });

        return () => unsubscribe();
    }, []);

    const handleDelete = async (dbPath) => {
        if (window.confirm("तुम्ही हे काम हटवू इच्छिता का? (Are you sure you want to delete this work?)")) {
            try {
                const workRef = dbRef(db, dbPath);
                await remove(workRef);
                toast.success("यशस्वीरीत्या हटवले!");
            } catch (error) {
                toast.error("त्रुटी: " + error.message);
            }
        }
    };

    const downloadExcel = () => {
        const excelData = filteredData.map((item, index) => ({
            'अ.क्र.': index + 1,
            'वर्क नंबर': item['वर्क नंबर'] || item.workNumber || '-',
            'तालुका': item.taluka || '-',
            'योजनेचे नाव': item.schemeName || '-',
            'कामाचे नाव': item.workName || '-',
            'अंदाजपत्रकीय रक्कम (रु)': item.tenderAmount || '-',
            'नोंदणी दिनांक': item.date || '-',
            'जावक नंबर / प्रमा नंबर': item.inwardNo || '-',
            'जावक दिनांक': item.inwardDate || '-',
            'स्थिती': item.status === 'lottery_completed' ? 'लॉटरी पूर्ण' : (item.status === 'Approved' ? 'मंजूर' : (item.status === 'Rejected' ? 'नाकारले' : 'प्रलंबित'))
        }));

        const ws = XLSX.utils.json_to_sheet(excelData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "WorkNames");
        XLSX.writeFile(wb, `Work_Names_List_${new Date().toLocaleDateString()}.xlsx`);
        toast.info("एक्सेल फाईल डाउनलोड होत आहे...");
    };

    const copyToClipboard = () => {
        const headers = ['अ.क्र.', 'वर्क नंबर', 'तालुका', 'योजनेचे नाव', 'कामाचे नाव', 'रकमत', 'दिनांक', 'स्थिती'];
        const rows = filteredData.map((item, index) => [
            index + 1,
            item['वर्क नंबर'] || item.workNumber || '-',
            item.taluka || '-',
            item.schemeName || '-',
            item.workName || '-',
            item.tenderAmount || '-',
            item.date || '-',
            item.status || 'Pending'
        ]);

        const content = [headers, ...rows].map(row => row.join('\t')).join('\n');
        navigator.clipboard.writeText(content);
        toast.success("टेबल डेटा कॉपी झाला!");
    };

    const handlePrint = () => {
        const printWindow = window.open('', '_blank');
        const content = `
            <html>
                <head>
                    <title>कामाची यादी - प्रिंट</title>
                    <style>
                        @import url('https://fonts.googleapis.com/css2?family=Noto+Sans+Marathi:wght@400;700&display=swap');
                        body { font-family: 'Noto Sans Marathi', sans-serif; padding: 20px; color: #333; }
                        h2 { text-align: center; color: #085e53; margin-bottom: 20px; border-bottom: 2px solid #085e53; padding-bottom: 10px; }
                        table { width: 100%; border-collapse: collapse; margin-top: 10px; font-size: 12px; }
                        th, td { border: 1px solid #ddd; padding: 12px 8px; text-align: left; }
                        th { background-color: #f4f7fe; color: #085e53; font-weight: bold; text-transform: uppercase; }
                        tr:nth-child(even) { background-color: #fafafa; }
                        .footer { margin-top: 30px; text-align: right; font-size: 10px; color: #666; font-weight: bold; }
                        .status-badge { font-weight: bold; color: #085e53; }
                    </style>
                </head>
                <body>
                    <h2>कामाची यादी (Work Names List)</h2>
                    <table>
                        <thead>
                            <tr>
                                <th>अ.क्र.</th>
                                <th>वर्क नंबर</th>
                                <th>तालुका</th>
                                <th>योजना</th>
                                <th>कामाचे नाव</th>
                                <th>रकमत</th>
                                <th>दिनांक</th>
                                <th>स्थिती</th>
                            </tr>
                        </thead>
                        <tbody>
                            ${filteredData.map((item, index) => `
                                <tr>
                                    <td>${index + 1}</td>
                                    <td>${item['वर्क नंबर'] || item.workNumber || '-'}</td>
                                    <td>${item.taluka || '-'}</td>
                                    <td>${item.schemeName || '-'}</td>
                                    <td>${item.workName || '-'}</td>
                                    <td>${item.tenderAmount || '-'}</td>
                                    <td>${item.date || '-'}</td>
                                    <td>${item.status || 'Pending'}</td>
                                </tr>
                            `).join('')}
                        </tbody>
                    </table>
                    <div class="footer">सांगली जिल्हा परिषद | दिनांक: ${new Date().toLocaleDateString('mr-IN')}</div>
                </body>
            </html>
        `;
        printWindow.document.write(content);
        printWindow.document.close();
        printWindow.print();
    };

    const filteredData = works.filter(item => 
        (item.workName || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (item.taluka || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (item.schemeName || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (item.workNumber || '').toLowerCase().includes(searchTerm.toLowerCase())
    );

    if (loading) {
        return (
            <EngineerLayout>
                <div className="min-h-screen flex items-center justify-center bg-[#f4f7fe]">
                    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#1a1a4d]"></div>
                </div>
            </EngineerLayout>
        );
    }

    return (
        <EngineerLayout>
            <div className="p-6 md:p-10 bg-gray-50/30 min-h-screen">
                {/* Dark Header Card */}
                <div className="bg-[#1a1c23] rounded-3xl p-10 mb-10 shadow-2xl relative overflow-hidden group">
                    <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -mr-32 -mt-32 blur-3xl transition-transform duration-1000 group-hover:scale-110"></div>
                    <div className="absolute bottom-0 left-0 w-32 h-32 bg-[#085e53]/10 rounded-full -ml-16 -mb-16 blur-2xl"></div>
                    
                    <div className="relative flex flex-col md:flex-row justify-between items-center gap-6">
                        <div className="flex items-center gap-6">
                            <button 
                                onClick={() => navigate('/engineer/work-names-list')}
                                className="bg-white/10 hover:bg-white/20 text-white p-4 rounded-2xl transition-all hover:scale-110 active:scale-90 backdrop-blur-sm border border-white/10"
                            >
                                <FaArrowLeft size={20} />
                            </button>
                            <div>
                                <h1 className="text-4xl font-bold text-white tracking-tight mb-3">कामाची यादी</h1>
                                <p className="text-gray-400 font-bold text-sm tracking-widest uppercase">
                                    सांगली जिल्हा परिषद / <span className="text-[#085e53]">कामाची यादी</span>
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Table Content Card */}
                <div className="bg-white rounded-[2rem] shadow-xl shadow-gray-200/50 border border-gray-100 overflow-hidden">
                    
                    {/* Advanced Toolbar */}
                    <div className="p-8 flex flex-col lg:flex-row justify-between items-center gap-8 bg-gray-50/30 border-b border-gray-100">
                        <div className="flex items-center gap-3 w-full lg:w-auto overflow-x-auto pb-4 lg:pb-0 scrollbar-hide">
                            <button 
                                onClick={copyToClipboard}
                                className="flex items-center gap-2 px-6 py-2.5 bg-white hover:bg-gray-50 text-gray-700 border border-gray-200 rounded-xl text-[13px] font-bold shadow-sm transition-all hover:scale-105 active:scale-95 whitespace-nowrap"
                            >
                                <FaCopy className="text-blue-500" /> कॉपी
                            </button>
                            <button 
                                onClick={downloadExcel}
                                className="flex items-center gap-2 px-6 py-2.5 bg-white hover:bg-gray-50 text-gray-700 border border-gray-200 rounded-xl text-[13px] font-bold shadow-sm transition-all hover:scale-105 active:scale-95 whitespace-nowrap"
                            >
                                <FaFileExcel className="text-green-500" /> एक्सेल
                            </button>
                            <button 
                                onClick={handlePrint}
                                className="flex items-center gap-2 px-6 py-2.5 bg-white hover:bg-gray-50 text-gray-700 border border-gray-200 rounded-xl text-[13px] font-bold shadow-sm transition-all hover:scale-105 active:scale-95 whitespace-nowrap"
                            >
                                <FaPrint className="text-gray-500" /> प्रिंट
                            </button>
                        </div>

                        <div className="relative w-full lg:w-96 group">
                            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                                <FaSearch className="text-gray-400 group-focus-within:text-[#085e53] transition-colors" />
                            </div>
                            <input 
                                type="text" 
                                className="w-full pl-12 pr-4 py-3.5 bg-white border border-gray-200 rounded-2xl text-sm font-bold focus:ring-4 focus:ring-[#085e53]/5 focus:border-[#085e53] outline-none transition-all shadow-sm"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                placeholder="कामाचे नाव, तालुका, योजनेचे नाव शोध..."
                            />
                        </div>
                    </div>

                    {/* Premium Table Content */}
                    <div className="overflow-x-auto scrollbar-thin scrollbar-thumb-gray-200 scrollbar-track-transparent">
                        <table className="w-full border-collapse">
                            <thead>
                                <tr className="bg-gray-50/50">
                                    <th className="px-6 py-5 text-[11px] font-bold text-gray-400 uppercase tracking-widest border-b border-gray-100 text-center">अ.क्र.</th>
                                    <th className="px-6 py-5 text-[11px] font-bold text-gray-400 uppercase tracking-widest border-b border-gray-100 text-center">वर्क नंबर</th>
                                    <th className="px-6 py-5 text-[11px] font-bold text-gray-400 uppercase tracking-widest border-b border-gray-100 text-center">तालुका</th>
                                    <th className="px-6 py-5 text-[11px] font-bold text-gray-400 uppercase tracking-widest border-b border-gray-100 text-center">योजनेचे नाव</th>
                                    <th className="px-6 py-5 text-[11px] font-bold text-gray-400 uppercase tracking-widest border-b border-gray-100 text-left min-w-[300px]">कामाचे नाव</th>
                                    <th className="px-6 py-5 text-[11px] font-bold text-gray-400 uppercase tracking-widest border-b border-gray-100 text-center">दिनांक</th>
                                    <th className="px-6 py-5 text-[11px] font-bold text-gray-400 uppercase tracking-widest border-b border-gray-100 text-center min-w-[200px]">जावक/प्रमा नंबर</th>
                                    <th className="px-6 py-5 text-[11px] font-bold text-gray-400 uppercase tracking-widest border-b border-gray-100 text-center">स्थिती</th>
                                    <th className="px-6 py-5 text-[11px] font-bold text-gray-400 uppercase tracking-widest border-b border-gray-100 text-center">कृती</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-50/80">
                                {filteredData.map((item, index) => (
                                    <tr key={item.id} className="hover:bg-blue-50/30 transition-all duration-300 group">
                                        <td className="px-6 py-6 text-sm font-bold text-gray-400 text-center">{index + 1}</td>
                                        <td className="px-6 py-6 text-sm text-center">
                                            <span className="font-bold text-[#085e53] bg-[#085e53]/5 px-3 py-1 rounded-lg uppercase tracking-wider">
                                                {item['वर्क नंबर'] || item.workNumber || item.id.toString().substring(0, 8)}
                                            </span>
                                        </td>
                                        <td className="px-6 py-6 text-sm font-bold text-gray-700 text-center">{item.taluka || '-'}</td>
                                        <td className="px-6 py-6 text-xs font-bold text-gray-400 text-center uppercase tracking-wider">{item.schemeName || '-'}</td>
                                        
                                        <td className="px-6 py-6 min-w-[300px]">
                                            <div className="flex items-start gap-4">
                                                <div className="p-3 bg-gray-50 rounded-xl text-gray-400 group-hover:bg-[#085e53] group-hover:text-white transition-all duration-300">
                                                    <FaBriefcase size={16} />
                                                </div>
                                                <div className="flex flex-col gap-1.5">
                                                    <span className="font-bold text-gray-800 text-sm leading-tight group-hover:text-[#085e53] transition-colors">
                                                        {item.workName || '-'}
                                                    </span>
                                                    <div className="flex items-center gap-2">
                                                        <span className="text-[10px] font-bold text-red-500 bg-red-50/50 px-2 py-0.5 rounded-md border border-red-100/50">
                                                            रकमत: ₹ {parseFloat(item.tenderAmount).toLocaleString('en-IN')} /-
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-6 text-[13px] font-bold text-gray-500 text-center">
                                            {item.date || '-'}
                                        </td>
                                        <td className="px-6 py-6 text-center">
                                            <div className="flex flex-col gap-1">
                                                <span className="text-gray-800 font-bold text-xs">{item.inwardNo || '-'}</span>
                                                <span className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">{item.inwardDate || '-'}</span>
                                            </div>
                                        </td>
                                        <td className="px-6 py-6 text-center">
                                            <span className={`px-4 py-2 rounded-xl text-[10px] font-bold uppercase tracking-widest shadow-sm border transition-all ${
                                                item.status === 'lottery_completed' || item.status === 'Approved'
                                                ? 'bg-emerald-50 text-emerald-700 border-emerald-100 group-hover:bg-emerald-500 group-hover:text-white' 
                                                : item.status === 'Rejected'
                                                ? 'bg-rose-50 text-rose-700 border-rose-100 group-hover:bg-rose-500 group-hover:text-white'
                                                : 'bg-amber-50 text-amber-700 border-amber-100 group-hover:bg-amber-500 group-hover:text-white'
                                            }`}>
                                                {item.status === 'lottery_completed' ? 'लॉटरी पूर्ण' : (item.status === 'Approved' ? 'मंजूर' : (item.status === 'Rejected' ? 'नाकारले' : 'प्रलंबित'))}
                                            </span>
                                        </td>

                                        <td className="px-6 py-6">
                                            <div className="flex items-center justify-center gap-2">
                                                <button 
                                                    onClick={() => navigate(`/engineer/work-names/edit/${item.category}/${item.id}`)}
                                                    className="p-3 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-xl transition-all hover:scale-110 shadow-sm hover:shadow-md border border-transparent hover:border-blue-100"
                                                    title="Edit"
                                                >
                                                    <FaEdit size={16} />
                                                </button>
                                                <button 
                                                    onClick={() => navigate(`/engineer/work-names/details/${item.category}/${item.id}`)}
                                                    className="p-3 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-xl transition-all hover:scale-110 shadow-sm hover:shadow-md border border-transparent hover:border-red-100"
                                                    title="View Details"
                                                >
                                                    <FaFilePdf size={16} />
                                                </button>
                                                <button 
                                                    onClick={() => handleDelete(item.dbPath)}
                                                    className="p-3 text-gray-400 hover:text-rose-600 hover:bg-rose-50 rounded-xl transition-all hover:scale-110 shadow-sm hover:shadow-md border border-transparent hover:border-rose-100"
                                                    title="Delete"
                                                >
                                                    <FaTrashAlt size={16} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        
                        {filteredData.length === 0 && (
                            <div className="py-24 text-center flex flex-col items-center gap-6">
                                <div className="p-8 bg-gray-50 rounded-full text-gray-200">
                                    <FaSearch size={64} />
                                </div>
                                <div className="space-y-2">
                                    <p className="text-gray-500 font-bold text-xl tracking-tight">कोणतेही काम आढळले नाही</p>
                                    <p className="text-gray-400 text-sm font-bold uppercase tracking-widest">तुमच्या शोध संज्ञेसह पुन्हा प्रयत्न करा</p>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Footer Info */}
                    <div className="p-8 border-t border-gray-50 bg-gray-50/30 flex flex-col md:flex-row justify-between items-center gap-4 text-[11px] font-bold text-gray-400 tracking-widest uppercase">
                        <div className="flex items-center gap-3">
                            <span className="bg-[#085e53]/10 text-[#085e53] px-3 py-1 rounded-lg">एकूण कामे: {filteredData.length}</span>
                        </div>
                        <div className="flex items-center gap-2 opacity-60">
                            <FaCalendarAlt /> शेवटचे अपडेट: {new Date().toLocaleDateString('mr-IN')}
                        </div>
                    </div>
                </div>
            </div>
        </EngineerLayout>
    );
};

export default EngineerWorkNames;
